import type {
  PartnerCarrierAccountCreateV2,
  PartnerCarrierAccountUpdateV2,
  PartnerCarrierAccountV2,
  PartnerSearchCarrierAccountsV2Params,
} from '@shipium-corp/shipium-ts-types';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import {
  CarrierAccountGatewayOrchestrationRequestTypeBox,
  CarrierAccountGatewayOrchestrationResponseTypeBox,
} from '@shipium-corp/shipium-ts-types/schemas';
import type { Static } from '@sinclair/typebox';
import GatewayAxiosClient from '~/store/services/GatewayAxiosClient';
import { type LinkAssetPayload, type BulkLinkPayloads, linkMultipleAssets } from '../api-utils';
import type { AxiosResponse } from 'axios';

// Types inferred from TypeBox schemas
export type CarrierAccountGatewayOrchestrationRequest = Static<typeof CarrierAccountGatewayOrchestrationRequestTypeBox>;
export type CarrierAccountGatewayOrchestrationResponse = Static<
  typeof CarrierAccountGatewayOrchestrationResponseTypeBox
>;

type SingleAssetLinkType =
  | 'usppiContact'
  | 'usppiAddress'
  | 'sortFacilityContact'
  | 'sortFacilityAddress'
  | 'shipperContacts'
  | 'shipperAddresses'
  | 'returnToContact'
  | 'returnToAddress';

type ArrayAssetLinkType = 'tags';

export const carrierAccounts = {
  search: {
    queryKey: 'partner-management-service/carrier-accounts',
    configurationType: 'default' as const,
    fn: async (params: PartnerSearchCarrierAccountsV2Params = {}) => {
      const { data } = await GatewayAxiosClient.get<PartnerCarrierAccountV2[]>(
        '/partner-management-proxy/api/v2/partners/carrierAccounts',
        {
          params,
        },
      );

      return data;
    },
  },
  get: {
    queryKey: 'partner-management-service/carrier-accounts/id',
    configurationType: 'no-cache' as const,
    fn: async (carrierAccountId: string) => {
      const { data } = await GatewayAxiosClient.get<PartnerCarrierAccountV2>(
        `/partner-management-proxy/api/v2/partners/carrierAccounts/${carrierAccountId}`,
      );

      return data;
    },
  },
  create: async (carrierAccountCreate: PartnerCarrierAccountCreateV2) => {
    const { data } = await GatewayAxiosClient.post<PartnerCarrierAccountV2>(
      '/partner-management-proxy/api/v2/partners/carrierAccounts',
      carrierAccountCreate,
    );
    return {
      data,
      invalidateQueryKeys: [
        ['partner-management-service/carrier-accounts'],
        ['orchestration-service/carriers-and-methods'],
        ['orchestration-service/carrier-summaries'],
      ],
    };
  },
  update: async ({
    carrierAccountId,
    payload,
  }: {
    carrierAccountId: string;
    payload: PartnerCarrierAccountUpdateV2;
  }) => {
    const { data } = await GatewayAxiosClient.put<PartnerCarrierAccountV2>(
      `/partner-management-proxy/api/v2/partners/carrierAccounts/${carrierAccountId}`,
      payload,
    );

    return {
      data,
      invalidateQueryKeys: [
        ['partner-management-service/carrier-accounts'],
        ['partner-management-service/carrier-accounts/id', carrierAccountId],
        ['orchestration-service/carriers-and-methods'],
        ['orchestration-service/carrier-summaries'],
      ],
    };
  },
  link: async ({
    carrierAccountId,
    payload,
  }: {
    carrierAccountId: string;
    payload: LinkAssetPayload<SingleAssetLinkType | ArrayAssetLinkType>;
  }) => {
    await GatewayAxiosClient({
      method: payload.linkAction === 'link' ? 'PUT' : 'DELETE',
      url: `/partner-management-proxy/api/v2/partners/carrierAccounts/${carrierAccountId}/${payload.assetType}/${payload.assetId}`,
    });

    return {
      invalidateQueryKeys: [
        ['partner-management-service/carrier-accounts'],
        ['partner-management-service/carrier-accounts/id', carrierAccountId],
      ],
    };
  },
  bulkLink: async ({
    carrierAccount,
    payloads,
  }: {
    carrierAccount: PartnerCarrierAccountV2;
    payloads: BulkLinkPayloads<SingleAssetLinkType, ArrayAssetLinkType>;
  }) => {
    return await linkMultipleAssets<SingleAssetLinkType, ArrayAssetLinkType, PartnerCarrierAccountV2>(
      payloads,
      {
        shipperAddresses: carrierAccount.shipperAddressId,
        shipperContacts: carrierAccount.shipperContactId,
        returnToAddress: carrierAccount.returnToAddressId,
        returnToContact: carrierAccount.returnToContactId,
        usppiAddress: carrierAccount.unitedStatesPrincipalPartyInInterestAddressId,
        usppiContact: carrierAccount.unitedStatesPrincipalPartyInInterestContactId,
        tags: carrierAccount.tagIds,
        sortFacilityAddress: carrierAccount.sortFacilityAddressId,
        sortFacilityContact: carrierAccount.sortFacilityContactId,
      },
      (payload) => carrierAccounts.link({ carrierAccountId: carrierAccount.carrierAccountId!, payload }),
    );
  },
  gatewayOrchestration: async (payload: CarrierAccountGatewayOrchestrationRequest) => {
    const { data } = await GatewayAxiosClient.post<
      CarrierAccountGatewayOrchestrationRequest,
      AxiosResponse<CarrierAccountGatewayOrchestrationResponse>
    >(`/orchestration/partners/carrierAccounts`, payload);

    const contactIdQueryKeys = payload?.contactLinks?.map((link) => [
      'partner-management-service/contacts/id',
      link?.contactId || '',
    ]);
    const addressIdQueryKeys = payload?.addressLinks?.map((link) => [
      'partner-management-service/addresses/id',
      link?.addressId || '',
    ]);

    return {
      data,
      invalidateQueryKeys: [
        ['partner-management-service/carrier-accounts'],
        ['partner-management-service/carrier-accounts/id', data?.carrierAccountId || payload?.carrierAccountId],
        ['partner-management-service/contracts'],
        ['partner-management-service/contracts/id', payload?.contractId],
        ['partner-management-service/contacts'],
        ...(contactIdQueryKeys || []),
        ['partner-management-service/addresses'],
        ...(addressIdQueryKeys || []),
        ['orchestration-service/carriers-and-methods'],
        ['orchestration-service/carrier-summaries'],
        // Add fulfillment context invalidation since addresses/contacts are linked
        ['partner-management-service/fulfillment-contexts'],
        ['partner-management-service/fulfillment-contexts/id', payload?.fulfillmentContextId],
      ],
    };
  },
};
